"use client";

import { useState } from "react";
import { Reveal } from "@/components/marketing/Reveal";
import { useMarketingText } from "@/lib/i18n/marketingClient";

export type FeatureFaqItem = { q: string; a: string };

function FaqJsonLd({ items }: { items: FeatureFaqItem[] }) {
  const graph = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }}
    />
  );
}

export function FeatureFaq({
  heading = "Questions, answered plainly",
  items,
  includeJsonLd = true,
}: {
  heading?: string;
  items: FeatureFaqItem[];
  includeJsonLd?: boolean;
}) {
  const localize = useMarketingText();
  const localizedItems = items.map((item) => ({ q: localize(item.q), a: localize(item.a) }));
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-6xl mx-auto px-6 py-14 sm:py-16">
      <div className="max-w-3xl mx-auto">
      {includeJsonLd ? <FaqJsonLd items={localizedItems} /> : null}
      <Reveal>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{localize(heading)}</h2>
      </Reveal>
      <div className="mt-8 divide-y divide-border border-y border-border">
        {localizedItems.map((item, i) => {
          const isOpen = open === i;
          const panelId = `feature-faq-panel-${i}`;
          const buttonId = `feature-faq-button-${i}`;
          return (
            <div key={item.q}>
              <button
                id={buttonId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full text-left py-4 flex items-start justify-between gap-4"
              >
                <span className="font-medium">{item.q}</span>
                <span className="text-muted-foreground shrink-0">{isOpen ? "−" : "+"}</span>
              </button>
              <p
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!isOpen}
                className="pb-4 text-sm text-muted-foreground leading-relaxed"
              >
                {item.a}
              </p>
            </div>
          );
        })}
      </div>
      </div>
    </section>
  );
}
